import { useEffect, useState } from "react";
import { getIdea, putIdea } from "../data/ideaStore";
import type { ModelInfo } from "../data/modelStore";
import {
  type BuildSpec,
  type Idea,
  type Settings,
  critiquesOf,
  latestBuildSpec,
  personasOf,
} from "../data/types";
import { activeEngineLabel } from "../engine/active";
import { generateBuildSpec } from "../engine/buildSpec";

interface Props {
  ideaId: string;
  settings: Settings;
  installedModel: ModelInfo | null;
  onBack: () => void;
  onIdeaSaved: () => void;
}

type State =
  | { kind: "loading" }
  | { kind: "noIdea" }
  | { kind: "idle"; idea: Idea; existing?: BuildSpec }
  | { kind: "generating"; idea: Idea }
  | { kind: "error"; idea: Idea; message: string };

export function BuildSpecScreen(props: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const idea = await getIdea(props.ideaId);
      if (cancelled) return;
      if (!idea) return setState({ kind: "noIdea" });
      setState({ kind: "idle", idea, existing: latestBuildSpec(idea) });
    })();
    return () => {
      cancelled = true;
    };
  }, [props.ideaId]);

  const handleGenerate = async () => {
    if (state.kind !== "idle" && state.kind !== "error") return;
    const base = state.idea;
    setState({ kind: "generating", idea: base });
    const outcome = await generateBuildSpec(base, props.settings, props.installedModel);
    if (outcome.kind === "error") {
      setState({ kind: "error", idea: base, message: outcome.message });
      return;
    }
    const updated: Idea = {
      ...base,
      buildSpecs: [...(base.buildSpecs ?? []), outcome.spec],
      state: "SpecReady",
      updatedAtMs: Date.now(),
    };
    await putIdea(updated);
    props.onIdeaSaved();
    setState({ kind: "idle", idea: updated, existing: outcome.spec });
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy build spec:", text);
    }
  };

  const handleDownload = (spec: BuildSpec, idea: Idea) => {
    const blob = new Blob([spec.markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = idea.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    a.download = `${safeName || "build-spec"}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">Build spec</h1>
        <span className="w-16" />
      </header>

      <main className="flex flex-1 flex-col gap-4 px-4 py-4">
        {state.kind === "loading" && <div className="text-sm text-ink-muted">Loading…</div>}
        {state.kind === "noIdea" && (
          <div className="text-sm text-rose-700">That idea is gone.</div>
        )}

        {state.kind !== "loading" && state.kind !== "noIdea" && (
          <>
            <section className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">{state.idea.title}</h2>
              <div className="text-xs text-ink-muted">
                Engine: {activeEngineLabel(props.settings, props.installedModel)}
              </div>
            </section>

            <Preflight idea={state.idea} />

            {state.kind === "generating" && (
              <div className="card flex flex-col gap-2 p-4">
                <div className="text-sm font-medium">Synthesizing build spec…</div>
                <div className="text-xs text-ink-muted">
                  This is the longest call — the model writes a full Markdown spec. Cloud usually
                  finishes in 10–30 s; on-device Gemma can take longer.
                </div>
              </div>
            )}

            {state.kind === "error" && (
              <div className="rounded-xl bg-rose-50 p-4 text-sm text-rose-900 ring-1 ring-rose-200">
                <div className="font-semibold">Build spec generation failed</div>
                <p className="mt-1 whitespace-pre-wrap">{state.message}</p>
                <button className="btn-ghost mt-3" onClick={handleGenerate}>
                  Retry
                </button>
              </div>
            )}

            {state.kind === "idle" && (
              <>
                {state.existing ? (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <button className="btn-primary" onClick={handleGenerate}>
                        Regenerate
                      </button>
                      <button className="btn-ghost" onClick={() => handleCopy(state.existing!.markdown)}>
                        {copied ? "Copied ✓" : "Copy Markdown"}
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => handleDownload(state.existing!, state.idea)}
                      >
                        Download .md
                      </button>
                    </div>
                    <div className="text-xs text-ink-muted">
                      Generated{" "}
                      {new Date(state.existing.generatedAtMs).toLocaleString()} via{" "}
                      {state.existing.source}
                    </div>
                    <article className="card prose-spec p-4">
                      <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">
                        {state.existing.markdown}
                      </pre>
                    </article>
                  </>
                ) : (
                  <div className="card flex flex-col gap-3 p-4">
                    <p className="text-sm">
                      One synthesis call produces a paste-ready Markdown spec — sections include
                      product summary, target user, key features (each tagged with the persona
                      that motivated them), non-goals (tagged with persona dealbreakers), tech
                      stack, v1 scope, deferred, build order, open risks, and a Mom-Test
                      interview script.
                    </p>
                    <p className="text-sm text-ink-muted">
                      Output is meant to paste directly into Cursor / Claude Code / Lovable.
                    </p>
                    <button className="btn-primary self-start" onClick={handleGenerate}>
                      Generate build spec
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </>
  );
}

function Preflight(props: { idea: Idea }) {
  const c = (props.idea.clarifications ?? []).length;
  const p = personasOf(props.idea).length;
  const cr = critiquesOf(props.idea).length;
  const missing: string[] = [];
  if (c === 0) missing.push("clarifications");
  if (p === 0) missing.push("personas");
  if (cr === 0) missing.push("critiques");
  if (missing.length === 0) return null;
  return (
    <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-200">
      The spec is sharper with everything upstream filled in. Currently missing:{" "}
      <strong>{missing.join(", ")}</strong>. You can still generate, but the output will lean
      heavier on assumptions.
    </div>
  );
}
